import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, Image, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  pending: { label: 'Menunggu', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Disetujui', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Ditolak', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function PaymentConfirmations() {
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  const load = (status) => {
    setLoading(true);
    api.get(`/tenant/payment-confirmations?status=${status}`).then(r => { setConfirmations(r.data.confirmations); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(tab); }, [tab]);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const handleApprove = async (id) => {
    if (!window.confirm('Setujui pembayaran ini? Order akan diubah menjadi PAID.')) return;
    try { await api.put(`/tenant/payment-confirmations/${id}/approve`); toast.success('Pembayaran disetujui'); load(tab); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Tolak konfirmasi pembayaran ini?')) return;
    try { await api.put(`/tenant/payment-confirmations/${id}/reject`); toast.success('Konfirmasi ditolak'); load(tab); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
  };

  const tabs = ['pending', 'approved', 'rejected'];

  return (
    <div data-testid="payment-confirmations-page">
      <h1 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>Konfirmasi Transfer</h1>

      <div className="flex gap-2 mb-6">
        {tabs.map(t => {
          const s = STATUS_MAP[t];
          return (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === t ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {s.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Memuat...</div>
      ) : confirmations.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-12 text-center text-slate-400">
          <Clock size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Tidak ada konfirmasi {STATUS_MAP[tab]?.label?.toLowerCase()}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {confirmations.map(c => {
            const s = STATUS_MAP[c.status] || STATUS_MAP.pending;
            const SIcon = s.icon;
            return (
              <Card key={c.id} className="border-slate-200 shadow-sm">
                <CardContent className="pt-5">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono font-bold text-sm">#{c.order_number}</span>
                        <Badge className={`${s.color} text-xs`}><SIcon size={12} className="mr-1" /> {s.label}</Badge>
                        <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString('id-ID')}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400 text-xs">Bank</p>
                          <p className="font-medium">{c.bank_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs">Atas Nama</p>
                          <p className="font-medium">{c.account_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs">Jumlah</p>
                          <p className="font-bold text-emerald-600">Rp {fmt(c.amount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs">Tanggal Transfer</p>
                          <p className="font-medium">{c.transfer_date || '-'}</p>
                        </div>
                      </div>
                      <div className="text-sm">
                        <p className="text-slate-400 text-xs">Email</p>
                        <p>{c.customer_email}</p>
                      </div>
                      {c.notes && <p className="text-sm text-slate-500 italic">"{c.notes}"</p>}
                      {c.proof_url && (
                        <a href={c.proof_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
                          <Image size={14} /> Lihat Bukti Transfer <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                    {c.status === 'pending' && (
                      <div className="flex gap-2 lg:flex-col">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprove(c.id)}>
                          <CheckCircle size={14} className="mr-1" /> Setujui
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleReject(c.id)}>
                          <XCircle size={14} className="mr-1" /> Tolak
                        </Button>
                      </div>
                    )}
                    {c.status === 'approved' && c.approved_at && (
                      <div className="text-right text-xs text-slate-400">
                        <p>Disetujui: {new Date(c.approved_at).toLocaleString('id-ID')}</p>
                        <p>Oleh: {c.approved_by}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
